import React, {useMemo} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Detail, Label, Text} from '@forecast-it/design-system';
import {NovaAgentDraft} from '../state/useNovaAgentDraft';
import {PromptTarget} from '../state/useNovaPromptState';
import {NovaVariable} from '../shared/novaVariableLibrary';
import {MonospacePromptEditor} from './MonospacePromptEditor';
import {NovaMcpTools} from './NovaMcpTools';
import {NovaWorkflowVariableLibrary, StudioWorkflowVariableGroup, validateWorkflowInput} from './NovaWorkflowVariableLibrary';
import {NovaInputIssues} from './NovaInputIssues';
import {useAgentMdTargetClaim} from './usePromptTargetClaim';
import {useNovaMcpTools} from '../shared/useNovaMcpTools';

// 24px under the explainer, the two libraries 12px apart, then the editor's own label row 24px below
// them and 8px above the box.
const Container = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-height: 0;
`;
const Explainer = styled.div`
	margin-bottom: 24px;
`;
const SecondLibrary = styled.div`
	margin-top: 12px;
`;
// The input's name with its optional note beside it, on one line.
const EditorLabelRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 24px 0 8px;
`;

// Tall enough to work with a multi-section agent.md without immediately needing to scroll.
const EDITOR_HEIGHT = 430;

interface Props {
	draft: NovaAgentDraft;
	variableGroups: StudioWorkflowVariableGroup[];
	declarationFailed: boolean;
	// The declaration has not been read yet: no token is flagged and the library claims no count.
	declarationLoading?: boolean;
	// Reads the declaration again after a failed read.
	onRetryDeclaration?: () => void;
	// The workflow's ready files, each citable as a {file:...} variable, among the library variables.
	variables: NovaVariable[];
}

// An agentic workflow's agent.md: its optional standing context, added to the System prompt on every
// run. In order: what agent.md is for, the Variable library and the MCP tools (each naming agent.md as
// where a click inserts), then the editor. Every variable, file and tool is insertable here.
//
// Nothing on this tab is required. An empty agent.md saves as empty and reports nothing at all.
export function NovaWorkflowAgentMdTab({
	draft,
	variableGroups,
	declarationFailed,
	declarationLoading = false,
	onRetryDeclaration,
	variables,
}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	const {values, setValue, setActiveTarget, insertTokenAtCursor, lastInsertion} = draft.promptState;
	useAgentMdTargetClaim(draft.promptState);
	const mcpTools = useNovaMcpTools();
	const declarationUnknown = declarationFailed || declarationLoading;

	const fileVariables = useMemo(() => variables.filter(variable => variable.scope === 'FILES'), [variables]);
	const issues = useMemo(
		() =>
			validateWorkflowInput(
				values.agentMd,
				PromptTarget.AgentMd,
				variableGroups,
				fileVariables,
				declarationUnknown,
				mcpTools
			),
		[values.agentMd, variableGroups, fileVariables, declarationUnknown, mcpTools]
	);
	const targetLabel = formatMessage({id: 'nova.variableLibrary.targetAgentMd'});

	return (
		<Container data-cy="nova-agent-md-tab" data-testid="nova-agent-md-tab">
			<Explainer>
				<Text color="auxiliary" data-cy="nova-agent-md-explainer">
					{formatMessage({id: 'nova.agentMd.explainer'})}
				</Text>
			</Explainer>
			<NovaWorkflowVariableLibrary
				variableGroups={variableGroups}
				fileVariables={fileVariables}
				loadFailed={declarationFailed}
				loading={declarationLoading}
				onRetry={onRetryDeclaration}
				helper={formatMessage({id: 'nova.variableLibrary.hintAgentMd'})}
				targetLabel={targetLabel}
				onInsert={token => insertTokenAtCursor(token)}
			/>
			<SecondLibrary>
				<NovaMcpTools
					mcpTools={mcpTools}
					onRetry={mcpTools.retry}
					helper={formatMessage({id: 'nova.mcpTools.hintAgentMd'})}
					targetLabel={targetLabel}
					onInsert={token => insertTokenAtCursor(token)}
				/>
			</SecondLibrary>
			<EditorLabelRow data-cy="nova-agent-md-label">
				<Label>{formatMessage({id: 'nova.agentMd.label'})}</Label>
				<Detail size="1" color="auxiliary">
					{formatMessage({id: 'nova.agentMd.helperText'})}
				</Detail>
			</EditorLabelRow>
			<MonospacePromptEditor
				dataCy="nova-agent-md"
				target={PromptTarget.AgentMd}
				value={values.agentMd}
				height={EDITOR_HEIGHT}
				placeholder={formatMessage({id: 'nova.agentMd.placeholder'})}
				onChange={(value: string) => setValue(PromptTarget.AgentMd, value)}
				onActivate={setActiveTarget}
				insertion={lastInsertion?.target === PromptTarget.AgentMd ? lastInsertion : null}
				issues={issues.length > 0 ? <NovaInputIssues issues={issues} dataCy="nova-agent-md-issues" /> : undefined}
			/>
		</Container>
	);
}
